import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
function getLogoBase64() {
  return new Promise((resolve, reject) => {
    fetch('/Proline Logo HQ.png')
      .then(r => r.blob())
      .then(blob => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      })
      .catch(reject);
  });
}

const EWN_WORDING = `EARLY WARNING NOTICE\n\nThis document serves as an Early Warning Notice pursuant to the contract conditions. The Contractor hereby gives notice of a matter which could:\n(a) increase the total of the Prices,\n(b) delay Completion or a Key Date,\n(c) delay meeting a Condition for a bonus, or\n(d) impair the performance of the works in use.\n\nThe Subcontractor reserves all rights and entitlements under the subcontract. This notice is issued without prejudice to any other rights and remedies available. The works described herein fall outside the agreed subcontract scope and will require separate assessment and valuation.`;

const CVI_WORDING = `CONFIRMATION OF VERBAL INSTRUCTION\n\nThis document confirms a verbal instruction received on the date and time stated above. The Subcontractor has carried out or will carry out the instructed works under protest, reserving all rights to recover additional costs and extensions of time as appropriate.\n\nThe works described herein constitute a change to the agreed subcontract scope. The Subcontractor hereby notifies the Main Contractor that costs will be tracked and a variation account will be submitted for agreement. All additional resources deployed shall be recorded on contemporaneous daywork sheets.`;

const IR_WORDING = `INSTRUCTION RECEIVED\n\nThis records a formal instruction received from the Main Contractor. The attached document(s) constitute the instruction. Proline Internals Ltd will proceed in accordance with the instruction subject to agreement of any additional costs and/or time.`;

const LEGAL_WORDING = { EWN: EWN_WORDING, CVI: CVI_WORDING, IR: IR_WORDING };

function fmt(n) {
  return `£${(n || 0).toFixed(2)}`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function formatHHMM(t) {
  if (!t) return '—';
  return String(t).substring(0, 5);
}

function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

// Try to load the logo as an HTMLImageElement for direct addImage use.
// Falls back to null if the image can't be loaded.
function loadLogoImage(dataUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

function triggerBlobDownload(doc, fileName) {
  const blob = doc.output('blob');
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function generateDayworkPDF({
  reference,
  dateOfWorks,
  status,
  projectName,
  projectRef,
  mcName,
  location,
  level,
  gridlines,
  instructionRef,
  instructionType,
  description,
  raisedBy,
  createdDate,
  labour,
  materials,
  plant,
  rates,
  instructionDetail,
  instructionAttachments,
  mcInstructionAuthority,
  worksDescription,
  reasonOutsideScope,
}) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 14;
  const contentW = pageW - margin * 2;

  // ── HEADER ───────────────────────────────────────────────────────────────────

  // Try loading the logo as an img element so the browser decodes the PNG
  const logoImg = await loadLogoImage(await getLogoBase64());
  if (logoImg) {
    try {
      doc.addImage(logoImg, 'PNG', margin, 10, 45, 18);
    } catch (_) {
      // PNG decode failed inside jsPDF — fall back to styled text logo
      drawTextLogo(doc, margin);
    }
  } else {
    drawTextLogo(doc, margin);
  }

  doc.setFontSize(7);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(100);
  doc.text('Proline Internals Ltd', margin, 31);
  doc.setFontSize(6);
  doc.text('Commercial Management System', margin, 35);

  // RIGHT: Reference box
  const boxW = 58;
  const boxX = pageW - margin - boxW;
  const boxY = 10;
  const boxH = 13;

  doc.setFillColor(26, 26, 46);
  doc.rect(boxX, boxY, boxW, boxH, 'F');
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text(reference || '—', boxX + boxW / 2, boxY + 8.5, { align: 'center' });

  doc.setFont(undefined, 'normal');
  doc.setFontSize(8);
  doc.setTextColor(50);
  doc.text(`Date of works: ${fmtDate(dateOfWorks)}`, boxX + boxW / 2, boxY + boxH + 5.5, { align: 'center' });

  const statusBg = {
    draft: [230, 232, 238],
    submitted: [219, 234, 254],
    signed: [209, 250, 229],
    disputed: [254, 226, 226],
  }[status] || [230, 232, 238];
  const statusTxt = {
    draft: [55, 65, 81],
    submitted: [30, 64, 175],
    signed: [6, 78, 59],
    disputed: [153, 27, 27],
  }[status] || [55, 65, 81];

  const badgeW = 32;
  const badgeX = boxX + (boxW - badgeW) / 2;
  const badgeY = boxY + boxH + 9;
  doc.setFillColor(...statusBg);
  doc.roundedRect(badgeX, badgeY, badgeW, 6.5, 1.5, 1.5, 'F');
  doc.setFontSize(7);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(...statusTxt);
  doc.text((status || 'DRAFT').toUpperCase(), badgeX + badgeW / 2, badgeY + 4.5, { align: 'center' });

  // ── DIVIDER ──────────────────────────────────────────────────────────────────
  const ruleY = 42;
  doc.setDrawColor(190);
  doc.setLineWidth(0.4);
  doc.line(margin, ruleY, pageW - margin, ruleY);

  // ── SINGLE-COLUMN INFO BLOCK ──────────────────────────────────────────────────
  let infoY = ruleY + 7;
  const lh = 6;
  const col2X = margin + contentW / 2;

  function infoLabel(x, y, label) {
    doc.setFontSize(8);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(80);
    doc.text(label + ':', x, y);
    return doc.getTextWidth(label + ': ');
  }

  function infoValue(x, y, value, maxW) {
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(30);
    const lines = doc.splitTextToSize(String(value || '—'), maxW);
    doc.text(lines.slice(0, 3), x, y);
    return lh * Math.max(1, Math.min(3, lines.length));
  }

  // Row 1: Project (full width)
  {
    const lw = infoLabel(margin, infoY, 'Project');
    infoValue(margin + lw, infoY, projectName || projectRef || '—', contentW - lw);
    infoY += lh;
  }

  // Row 2: Main Contractor | Instruction
  {
    const lw1 = infoLabel(margin, infoY, 'Main Contractor');
    infoValue(margin + lw1, infoY, mcName || '—', contentW / 2 - lw1 - 4);
    const insStr = instructionRef
      ? `${instructionRef}${instructionType ? '  (' + instructionType + ')' : ''}`
      : '—';
    const lw2 = infoLabel(col2X, infoY, 'Instruction');
    infoValue(col2X + lw2, infoY, insStr, contentW / 2 - lw2);
    infoY += lh;
  }

  // Row 3: Location | Level
  {
    const lw1 = infoLabel(margin, infoY, 'Location');
    infoValue(margin + lw1, infoY, location || '—', contentW / 2 - lw1 - 4);
    if (level || gridlines) {
      const levelStr = [level && `Level: ${level}`, gridlines && `Gridlines: ${gridlines}`].filter(Boolean).join('  |  ');
      const lw2 = infoLabel(col2X, infoY, 'Level / Gridlines');
      infoValue(col2X + lw2, infoY, levelStr, contentW / 2 - lw2);
    }
    infoY += lh;
  }

  // Row 5: Raised by | Date of Works
  {
    const lw1 = infoLabel(margin, infoY, 'Raised by');
    infoValue(margin + lw1, infoY, raisedBy || '—', contentW / 2 - lw1 - 4);
    if (createdDate) {
      const lw2 = infoLabel(col2X, infoY, 'Created');
      infoValue(col2X + lw2, infoY, fmtDate(createdDate), contentW / 2 - lw2);
    }
    infoY += lh;
  }

  // Row 4: Description (label + grey box)
  infoY += 2;
  doc.setFontSize(8);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(80);
  doc.text('Description of Works:', margin, infoY);
  infoY += 5;

  const descLines = doc.splitTextToSize(description || '—', contentW - 8);
  const descBoxH = Math.max(10, descLines.length * 4.5 + 5);
  doc.setFillColor(248, 249, 250);
  doc.setDrawColor(210);
  doc.setLineWidth(0.3);
  doc.rect(margin, infoY, contentW, descBoxH);
  doc.setFontSize(8);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(30);
  doc.text(descLines, margin + 4, infoY + 5);
  infoY += descBoxH + 8;

  let nextY = infoY;

  // ── HELPERS ───────────────────────────────────────────────────────────────────
  function sectionHeading(label, y) {
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(26, 26, 46);
    doc.text(label, margin, y);
  }

  const tableMargin = { left: margin, right: margin };
  const headStyles = { fillColor: [26, 26, 46], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7 };
  const bodyStyles = { fontSize: 7, cellPadding: 2 };

  function drawAuthRow(label, value, y) {
    doc.setFont(undefined, 'bold');
    doc.setFontSize(8);
    doc.setTextColor(80);
    doc.text(label + ':', margin + 4, y);
    const lw = doc.getTextWidth(label + ':  ');
    doc.setFont(undefined, 'normal');
    doc.setTextColor(30);
    doc.text(String(value || '—'), margin + 4 + lw, y);
    return y + lh;
  }

  // ── SECTION A: AUTHORITY FOR THESE WORKS ─────────────────────────────────────
  if (mcInstructionAuthority) {
    if (nextY > pageH - 70) { doc.addPage(); nextY = 18; }
    sectionHeading('A. Authority for These Works', nextY);
    nextY += 6;
    const authBoxStartY = nextY;
    let ay = nextY + 5;
    if (mcInstructionAuthority.reference) ay = drawAuthRow('Reference', mcInstructionAuthority.reference, ay);
    if (mcInstructionAuthority.type) ay = drawAuthRow('Type', mcInstructionAuthority.type, ay);
    if (mcInstructionAuthority.date) ay = drawAuthRow('Date', fmtDate(mcInstructionAuthority.date), ay);
    if (mcInstructionAuthority.time) ay = drawAuthRow('Time', mcInstructionAuthority.time, ay);
    const authInstructedBy = [mcInstructionAuthority.instructed_by_name, mcInstructionAuthority.instructed_by_role].filter(Boolean).join(' — ');
    if (authInstructedBy) ay = drawAuthRow('Instructed by', authInstructedBy, ay);
    if (mcInstructionAuthority.instruction_method) ay = drawAuthRow('Method', mcInstructionAuthority.instruction_method, ay);
    if (mcInstructionAuthority.main_contractor) ay = drawAuthRow('Main Contractor', mcInstructionAuthority.main_contractor, ay);
    ay += 2;
    const authBoxH = ay - authBoxStartY;
    doc.setDrawColor(200);
    doc.setLineWidth(0.3);
    doc.rect(margin, authBoxStartY, contentW, authBoxH);
    nextY = authBoxStartY + authBoxH + 8;
  }

  // ── SECTION B: DESCRIPTION OF WORKS ──────────────────────────────────────────
  if (worksDescription) {
    if (nextY > pageH - 50) { doc.addPage(); nextY = 18; }
    sectionHeading('B. Description of Works to be Carried Out', nextY);
    nextY += 6;
    const wdLines = doc.splitTextToSize(worksDescription, contentW - 8);
    const wdBoxH = Math.max(12, wdLines.length * 4.5 + 6);
    doc.setFillColor(248, 249, 250);
    doc.setDrawColor(210);
    doc.setLineWidth(0.3);
    doc.rect(margin, nextY, contentW, wdBoxH);
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(30);
    doc.text(wdLines, margin + 4, nextY + 5);
    nextY += wdBoxH + 8;
  }

  // ── SECTION C: REASON OUTSIDE SCOPE ──────────────────────────────────────────
  if (reasonOutsideScope) {
    if (nextY > pageH - 50) { doc.addPage(); nextY = 18; }
    sectionHeading('C. Reason Works Are Outside Subcontract Scope', nextY);
    nextY += 6;
    const rosLines = doc.splitTextToSize(reasonOutsideScope, contentW - 14);
    const rosBoxH = Math.max(12, rosLines.length * 4.5 + 6);
    doc.setFillColor(255, 251, 235);
    doc.setDrawColor(217, 119, 6);
    doc.setLineWidth(0.3);
    doc.rect(margin, nextY, contentW, rosBoxH);
    doc.setDrawColor(217, 119, 6);
    doc.setLineWidth(1.5);
    doc.line(margin, nextY, margin, nextY + rosBoxH);
    doc.setLineWidth(0.3);
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(30);
    doc.text(rosLines, margin + 6, nextY + 5);
    nextY += rosBoxH + 8;
  }

  // ── LABOUR TABLE ──────────────────────────────────────────────────────────────
  if (labour && labour.length > 0) {
    if (nextY > pageH - 65) { doc.addPage(); nextY = 18; }

    sectionHeading('Labour', nextY);

    const labourRows = labour.map(r => {
      const hrs = parseFloat(r.hours_calculated) || 0;
      const rate = rates ? rates.getRateForTrade(r.trade) : 0;
      return [
        r.operative_name || '—',
        r.trade || '—',
        formatHHMM(r.start_time),
        formatHHMM(r.finish_time),
        hrs.toFixed(2),
        fmt(hrs * rate),
        r.activity_note || '',
      ];
    });
    const totalLabHrs = labour.reduce((s, r) => s + (parseFloat(r.hours_calculated) || 0), 0);
    const totalLabCost = rates
      ? labour.reduce((s, r) => s + (parseFloat(r.hours_calculated) || 0) * rates.getRateForTrade(r.trade), 0)
      : 0;

    autoTable(doc, {
      startY: nextY + 4,
      head: [['Operative', 'Trade', 'Start', 'Finish', 'Hours', 'Value', 'Activity']],
      body: [
        ...labourRows,
        [{ content: '', colSpan: 3 }, 'TOTAL', totalLabHrs.toFixed(2), fmt(totalLabCost), ''],
      ],
      styles: bodyStyles,
      headStyles,
      columnStyles: {
        0: { cellWidth: 35 },
        1: { cellWidth: 22 },
        2: { cellWidth: 16 },
        3: { cellWidth: 16 },
        4: { cellWidth: 16 },
        5: { cellWidth: 22 },
        6: { cellWidth: 'auto' },
      },
      didParseCell(data) {
        if (data.row.index === labourRows.length) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [232, 237, 242];
        }
      },
      margin: tableMargin,
    });
    nextY = doc.lastAutoTable.finalY + 10;
  }

  // ── MATERIALS TABLE ───────────────────────────────────────────────────────────
  const activeMat = (materials || []).filter(m => m.description?.trim());
  if (activeMat.length > 0) {
    if (nextY > pageH - 65) { doc.addPage(); nextY = 18; }

    sectionHeading('Materials', nextY);

    const matMult = rates ? 1 + rates.materialsUplift / 100 : 1;
    const upliftPct = rates ? rates.materialsUplift : 0;

    const matRows = activeMat.map(m => {
      const sub = (parseFloat(m.quantity) || 0) * (parseFloat(m.price_per_unit) || 0);
      return [m.description, m.quantity || '—', m.unit || '—', fmt(parseFloat(m.price_per_unit) || 0), fmt(sub), fmt(sub * matMult)];
    });
    const totalMatSub = activeMat.reduce((s, m) => s + (parseFloat(m.quantity) || 0) * (parseFloat(m.price_per_unit) || 0), 0);

    autoTable(doc, {
      startY: nextY + 4,
      head: [['Description', 'Qty', 'Unit', 'Rate', 'Subtotal', `Total (${upliftPct}% uplift)`]],
      body: [
        ...matRows,
        [{ content: '', colSpan: 4 }, 'TOTAL', fmt(totalMatSub * matMult)],
      ],
      styles: bodyStyles,
      headStyles,
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 16 },
        2: { cellWidth: 16 },
        3: { cellWidth: 22 },
        4: { cellWidth: 22 },
        5: { cellWidth: 30 },
      },
      didParseCell(data) {
        if (data.row.index === matRows.length) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [232, 237, 242];
        }
      },
      margin: tableMargin,
    });
    nextY = doc.lastAutoTable.finalY + 10;
  }

  // ── PLANT TABLE ───────────────────────────────────────────────────────────────
  const activePlant = (plant || []).filter(p => p.description?.trim());
  if (activePlant.length > 0) {
    if (nextY > pageH - 65) { doc.addPage(); nextY = 18; }

    sectionHeading('Plant & Equipment', nextY);

    const plantUpliftPct = rates ? rates.plantUplift : 0;
    const hasPlantUplift = plantUpliftPct > 0;
    const plantMult = hasPlantUplift ? 1 + plantUpliftPct / 100 : 1;

    const plantRows = activePlant.map(p => {
      const sub = (parseFloat(p.quantity) || 0) * (parseFloat(p.rate) || 0);
      const row = [p.description, p.quantity || '—', p.unit || 'Day', fmt(parseFloat(p.rate) || 0), fmt(sub)];
      if (hasPlantUplift) row.push(fmt(sub * plantMult));
      return row;
    });
    const totalPlantSub = activePlant.reduce((s, p) => s + (parseFloat(p.quantity) || 0) * (parseFloat(p.rate) || 0), 0);

    const plantHead = hasPlantUplift
      ? [['Description', 'Qty', 'Unit', 'Rate', 'Subtotal', `Total (${plantUpliftPct}% uplift)`]]
      : [['Description', 'Qty', 'Unit', 'Rate', 'Total']];

    const plantTotalRow = hasPlantUplift
      ? [{ content: '', colSpan: 4 }, 'TOTAL', fmt(totalPlantSub * plantMult)]
      : [{ content: '', colSpan: 3 }, 'TOTAL', fmt(totalPlantSub)];

    autoTable(doc, {
      startY: nextY + 4,
      head: plantHead,
      body: [...plantRows, plantTotalRow],
      styles: bodyStyles,
      headStyles,
      columnStyles: hasPlantUplift ? {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 16 },
        2: { cellWidth: 16 },
        3: { cellWidth: 22 },
        4: { cellWidth: 22 },
        5: { cellWidth: 30 },
      } : {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 16 },
        2: { cellWidth: 16 },
        3: { cellWidth: 22 },
        4: { cellWidth: 30 },
      },
      didParseCell(data) {
        if (data.row.index === plantRows.length) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [232, 237, 242];
        }
      },
      margin: tableMargin,
    });
    nextY = doc.lastAutoTable.finalY + 10;
  }

  // ── GRAND TOTAL BOX ───────────────────────────────────────────────────────────
  if (nextY > pageH - 52) { doc.addPage(); nextY = 18; }

  const labCost = rates
    ? (labour || []).reduce((s, r) => s + (parseFloat(r.hours_calculated) || 0) * rates.getRateForTrade(r.trade), 0)
    : 0;
  const matCost = rates
    ? (materials || []).reduce((s, m) => s + (parseFloat(m.quantity) || 0) * (parseFloat(m.price_per_unit) || 0), 0) * (1 + rates.materialsUplift / 100)
    : 0;
  const plantCostTotal = rates
    ? (plant || []).reduce((s, p) => s + (parseFloat(p.quantity) || 0) * (parseFloat(p.rate) || 0), 0) * (1 + rates.plantUplift / 100)
    : 0;
  const grandTotal = labCost + matCost + plantCostTotal;

  const tbW = 88;
  const tbX = pageW - margin - tbW;
  const tbH = 36;

  doc.setDrawColor(180);
  doc.setLineWidth(0.4);
  doc.rect(tbX, nextY, tbW, tbH);

  doc.setFillColor(247, 248, 250);
  doc.rect(tbX + 0.4, nextY + 0.4, tbW - 0.8, tbH - 10 - 0.8, 'F');

  doc.setFontSize(8);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(60);

  const totRows = [
    ['Labour', labCost],
    [`Materials (incl. ${rates?.materialsUplift ?? 0}% uplift)`, matCost],
    [`Plant${rates?.plantUplift ? ` (incl. ${rates.plantUplift}% uplift)` : ''}`, plantCostTotal],
  ];
  totRows.forEach(([label, val], i) => {
    const ry = nextY + 7 + i * 6.5;
    doc.text(label + ':', tbX + 4, ry);
    doc.text(fmt(val), tbX + tbW - 4, ry, { align: 'right' });
  });

  doc.setDrawColor(180);
  doc.setLineWidth(0.3);
  doc.line(tbX, nextY + tbH - 10, tbX + tbW, nextY + tbH - 10);

  doc.setFillColor(26, 26, 46);
  doc.rect(tbX, nextY + tbH - 10, tbW, 10, 'F');
  doc.setFont(undefined, 'bold');
  doc.setFontSize(10);
  doc.setTextColor(255, 255, 255);
  doc.text('GRAND TOTAL:', tbX + 4, nextY + tbH - 3.5);
  doc.text(fmt(grandTotal), tbX + tbW - 4, nextY + tbH - 3.5, { align: 'right' });

  nextY += tbH + 12;

  // ── LINKED INSTRUCTION DETAILS ────────────────────────────────────────────────
  if (instructionDetail) {
    if (nextY > pageH - 80) { doc.addPage(); nextY = 18; }

    sectionHeading('Linked Instruction Details', nextY);
    nextY += 6;

    const infoBoxX = margin;
    const infoBoxStartY = nextY;

    doc.setFontSize(8);
    doc.setTextColor(30);

    function infoRow(label, value, y) {
      doc.setFont(undefined, 'bold');
      doc.setTextColor(80);
      doc.text(label + ':', infoBoxX + 4, y);
      const lw = doc.getTextWidth(label + ':  ');
      doc.setFont(undefined, 'normal');
      doc.setTextColor(30);
      doc.text(String(value || '—'), infoBoxX + 4 + lw, y);
      return y + lh;
    }

    let iy = nextY + 5;

    const typeLabel = {
      EWN: 'Early Warning Notice',
      CVI: 'Confirmation of Verbal Instruction',
      IR: 'Instruction Received',
    }[instructionDetail.type] || instructionDetail.type || '—';

    iy = infoRow('Reference', instructionDetail.reference, iy);
    iy = infoRow('Type', typeLabel, iy);
    iy = infoRow('Date Raised', fmtDate(instructionDetail.date), iy);

    const instructedBy = [instructionDetail.instructed_by_name, instructionDetail.instructed_by_role]
      .filter(Boolean).join('  —  ');
    if (instructedBy) iy = infoRow('Instructed by', instructedBy, iy);
    if (instructionDetail.instruction_method) iy = infoRow('Method', instructionDetail.instruction_method, iy);

    const locParts = [
      instructionDetail.location,
      instructionDetail.level && `Level: ${instructionDetail.level}`,
      instructionDetail.gridlines && `Gridlines: ${instructionDetail.gridlines}`,
    ].filter(Boolean);
    if (locParts.length) iy = infoRow('Location', locParts.join('  |  '), iy);

    iy += 2;

    // Instruction description
    if (instructionDetail.description) {
      doc.setFont(undefined, 'bold');
      doc.setTextColor(80);
      doc.text('Description:', infoBoxX + 4, iy);
      iy += 5;
      doc.setFont(undefined, 'normal');
      doc.setTextColor(30);
      const dLines = doc.splitTextToSize(instructionDetail.description, contentW - 12);
      doc.text(dLines, infoBoxX + 4, iy);
      iy += dLines.length * 4.5 + 5;
    }

    // Reason outside scope
    if (instructionDetail.reason_outside_scope) {
      doc.setFont(undefined, 'bold');
      doc.setTextColor(80);
      doc.text('Reason Outside Subcontract Scope:', infoBoxX + 4, iy);
      iy += 5;
      doc.setFont(undefined, 'normal');
      doc.setTextColor(30);
      const rLines = doc.splitTextToSize(instructionDetail.reason_outside_scope, contentW - 12);
      doc.text(rLines, infoBoxX + 4, iy);
      iy += rLines.length * 4.5 + 5;
    }

    // Legal wording
    const legalText = LEGAL_WORDING[instructionDetail.type];
    if (legalText) {
      iy += 2;
      doc.setFont(undefined, 'bold');
      doc.setTextColor(80);
      doc.text('Legal Wording:', infoBoxX + 4, iy);
      iy += 5;
      doc.setFont(undefined, 'italic');
      doc.setFontSize(7);
      doc.setTextColor(100);
      const lwLines = doc.splitTextToSize(legalText, contentW - 12);
      doc.text(lwLines, infoBoxX + 4, iy);
      iy += lwLines.length * 4 + 4;
      doc.setFontSize(8);
    }

    const infoBoxH = iy - infoBoxStartY + 2;
    doc.setDrawColor(200);
    doc.setLineWidth(0.3);
    doc.rect(infoBoxX, infoBoxStartY, contentW, infoBoxH);

    nextY = infoBoxStartY + infoBoxH + 10;
  }

  // ── INSTRUCTION ATTACHMENTS ───────────────────────────────────────────────────
  if (instructionDetail) {
    if (nextY > pageH - 50) { doc.addPage(); nextY = 18; }

    sectionHeading('Instruction Attachments', nextY);
    nextY += 6;

    const attachList = instructionAttachments || [];

    doc.setFontSize(8);
    if (attachList.length === 0) {
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100);
      doc.text('No digital attachments recorded. Physical documents held on file.', margin, nextY + 6);
      nextY += 14;
    } else {
      const attBoxStartY = nextY;
      let ay = nextY + 5;

      attachList.forEach((att, idx) => {
        const sizeStr = att.file_size ? ` (${formatBytes(att.file_size)})` : '';
        const dateStr = att.created_at ? `  —  uploaded ${fmtDate(att.created_at.split('T')[0])}` : '';
        const line = `${att.file_name || 'Attachment ' + (idx + 1)}${sizeStr}${dateStr}`;

        doc.setFont(undefined, 'bold');
        doc.setTextColor(26, 26, 46);
        doc.text('•', margin + 4, ay);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(30);
        const aLines = doc.splitTextToSize(line, contentW - 16);
        doc.text(aLines, margin + 8, ay);
        ay += aLines.length * 4.5 + 3;
      });

      ay += 3;
      doc.setFont(undefined, 'italic');
      doc.setTextColor(100);
      doc.setFontSize(7);
      doc.text(
        'Original instruction documents are stored in the Proline system and available on request.',
        margin + 4, ay,
      );
      ay += 6;
      doc.setFontSize(8);

      const attBoxH = ay - attBoxStartY + 2;
      doc.setDrawColor(200);
      doc.setLineWidth(0.3);
      doc.rect(margin, attBoxStartY, contentW, attBoxH);
      nextY = attBoxStartY + attBoxH + 10;
    }
  }

  // ── SIGNATURE PAGE ────────────────────────────────────────────────────────────
  doc.addPage();
  let sy = 18;

  doc.setFillColor(26, 26, 46);
  doc.rect(margin, sy, contentW, 12, 'F');
  doc.setFont(undefined, 'bold');
  doc.setFontSize(10);
  doc.setTextColor(255, 255, 255);
  doc.text(reference || '—', margin + 4, sy + 8);
  doc.setFontSize(8);
  doc.text(projectName || projectRef || '', pageW - margin - 4, sy + 8, { align: 'right' });
  sy += 16;

  const totalHrs = (labour || []).reduce((s, r) => s + (parseFloat(r.hours_calculated) || 0), 0);
  doc.setFontSize(8);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(60);
  doc.text(`Date of works: ${fmtDate(dateOfWorks)}   |   Total labour: ${totalHrs.toFixed(2)}h   |   Total value: ${fmt(grandTotal)}`, margin, sy);
  sy += 10;

  doc.setDrawColor(200);
  doc.setLineWidth(0.3);
  doc.line(margin, sy, pageW - margin, sy);
  sy += 8;

  const sigBoxH = 110;
  doc.setDrawColor(150);
  doc.setLineWidth(0.5);
  doc.rect(margin, sy, contentW, sigBoxH);

  doc.setFontSize(8);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(40);
  const introLines = doc.splitTextToSize(
    'By signing below, the Main Contractor confirms agreement with the hours, materials and plant recorded on this daywork sheet.',
    contentW - 8,
  );
  doc.text(introLines, margin + 4, sy + 8);
  sy += 8 + introLines.length * 5 + 4;

  doc.setFont(undefined, 'normal');
  doc.setTextColor(70);
  doc.setDrawColor(120);
  doc.setLineWidth(0.3);

  const labelX = margin + 4;
  const lineX2 = margin + contentW - 8;

  function sigLine(labelText, y) {
    doc.setFontSize(8);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(60);
    doc.text(labelText, labelX, y);
    const lw = doc.getTextWidth(labelText);
    doc.setDrawColor(120);
    doc.line(labelX + lw + 2, y + 1, lineX2, y + 1);
    return y + 12;
  }

  sy = sigLine('MC Representative Signature:', sy);
  sy = sigLine('Print Name:', sy);
  sy = sigLine('Position / Title:', sy);
  sy = sigLine('Company:', sy);
  sy = sigLine('Date:', sy);

  sy += 4;

  doc.setFontSize(8);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(40);
  const cbY = sy;
  const boxes = ['AGREED IN FULL', 'AGREED IN PART', 'DISPUTED'];
  let cbX = labelX;
  boxes.forEach(label => {
    doc.setDrawColor(100);
    doc.setLineWidth(0.4);
    doc.rect(cbX, cbY - 4, 5, 5);
    doc.text(label, cbX + 7, cbY);
    cbX += doc.getTextWidth(label) + 20;
  });
  sy = cbY + 12;

  doc.setFont(undefined, 'italic');
  doc.setTextColor(80);
  doc.text('If agreed in part or disputed, please state reason:', labelX, sy);
  sy += 6;
  doc.setFont(undefined, 'normal');
  doc.setDrawColor(120);
  doc.setLineWidth(0.3);
  doc.line(labelX, sy, lineX2, sy);
  sy += 8;
  doc.line(labelX, sy, lineX2, sy);

  // ── FOOTER ON EVERY PAGE ──────────────────────────────────────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  const genDate = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    const fy = pageH - 7;
    doc.setFontSize(6);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(130);
    doc.setDrawColor(200);
    doc.setLineWidth(0.25);
    doc.line(margin, fy - 3, pageW - margin, fy - 3);
    doc.text('Proline Internals Ltd — Confidential', margin, fy);
    doc.text(`Page ${p} of ${totalPages}`, pageW / 2, fy, { align: 'center' });
    doc.text(`Generated: ${genDate}`, pageW - margin, fy, { align: 'right' });
  }

  // ── BLOB-URL DOWNLOAD ─────────────────────────────────────────────────────────
  const fileName = `${reference}_Daywork_${dateOfWorks || 'unknown'}.pdf`;
  triggerBlobDownload(doc, fileName);
  return fileName;
}

// Styled text logo fallback — used when the PNG can't be decoded
function drawTextLogo(doc, margin) {
  doc.setFillColor(26, 26, 46);
  doc.rect(margin, 10, 45, 18, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('PROLINE', margin + 3, 22);
  doc.setFontSize(6);
  doc.setFont('helvetica', 'normal');
  doc.text('INTERNALS LTD', margin + 3, 27);
  doc.setTextColor(0, 0, 0);
}

export function openMailtoForDaywork({ reference, dateOfWorks, projectName, mcName, mcEmail, description, raisedBy }) {
  const subject = `Daywork Sheet ${reference} — ${projectName || ''}`;
  const body = [
    `Dear ${mcName || 'Sir/Madam'},`,
    '',
    `Please find attached Daywork Sheet ${reference} dated ${dateOfWorks ? new Date(dateOfWorks + 'T00:00:00').toLocaleDateString('en-GB') : ''}.`,
    '',
    `Project: ${projectName || '—'}`,
    `Description: ${description || '—'}`,
    '',
    'Please review, sign and return the attached PDF to confirm the works recorded.',
    '',
    'Kind regards,',
    `${raisedBy || 'Proline Internals Ltd'}`,
    'Proline Internals Ltd',
  ].join('\n');

  const mailto = `mailto:${mcEmail || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  window.open(mailto, '_blank');
}
